// ShipSpec — the single source of truth for a hull: its module stack, livery,
// and posture, as DATA. The 3D builder (scene/custos.ts) walks `modules` to
// compose named geometry groups; the SFB schematic (schematic.ts) can derive
// from the same spec by canonical `id` (next step — ids are aligned here so
// that merge is mechanical). Per docs/SHIP-ASSET-PIPELINE.md §2.1/§2.5 + the
// expert review on PR #8 (extract a shared ShipSpec; the module stack must be
// data, not inline conditionals).
//
// Convention: forward = -Z. Modules carry their spine position `z`.

import kbCargoshipRaw from './hulls/kb-cargoship.hull.json' with { type: 'json' };
import kbBattlecruiserRaw from './hulls/kb-battlecruiser.hull.json' with { type: 'json' };
import kbCommsStationRaw from './hulls/kb-comms-station.hull.json' with { type: 'json' };
import pupHullRaw from './hulls/pup-hull.hull.json' with { type: 'json' };
import custosTowerRaw from './hulls/custos-tower.hull.json' with { type: 'json' };
import cannibalHeavyRaw from './hulls/cannibal-heavy.hull.json' with { type: 'json' };
import spineTrussRaw from './hulls/spine-truss.hull.json' with { type: 'json' };
import lanceRaw from './hulls/lance.hull.json' with { type: 'json' };
import shipSpecOverridesRaw from './shipSpecOverrides.json' with { type: 'json' };
import { PART_NAMES, type PartName } from './partNames.ts';

// A runtime list as well as a union: hullManifest() validates an authored
// hull's declared module kinds against it.
export const MODULE_KINDS = [
  'drive',         // aft drive block: reactors + MPD nozzles + thin plumes (animated)
  'tankage',       // mid hull bulk + livery bands (+ humanitarian chevron)
  'collar',        // engineering collar (radiator hinge line)
  'taper',         // forward hull taper
  'bow',           // bow armor cap + ring ("the bow is the shield")
  'lance',         // spinal coilgun
  'vls',           // vertical missile launcher (cols × rows cells)
  'medicalPod',    // disarmed hospital pod (replaces vls)
  'sensor',        // sensor/EW cluster (standard | expanded)
  'boardingBay',   // boarding & inspection bay hatch
  'cargoPod',      // latched honey-amber cooperative cargo containers (civilian)
  'melissa',       // Pet-MELiSSA closed-loop bio chamber — green-glowing viewport
] as const;
export type ModuleKind = (typeof MODULE_KINDS)[number];

export interface ModuleSpec {
  id: string;
  kind: ModuleKind;
  /** spine position along Z (forward = -Z); off-axis mounts add their own offset in the builder */
  z: number;
  /** 'hull' = this module's geometry is part of the ship's authored hull
   *  (ShipSpec.hull), so the builder draws nothing for it. It exists for
   *  IDENTITY: damage localization, SSD cells, the schematic. Absent =
   *  procedural, exactly as today; a ship may mix the two. */
  geometry?: 'hull';
  cols?: number;       // vls
  rows?: number;       // vls
  expanded?: boolean;  // sensor — the EW picket fit
  /** size overrides so bigger hull classes are DATA, not new code (default = frigate) */
  length?: number;     // tankage / taper / lance / authored-module axial length
  size?: number;       // drive / bow uniform scale
}

export interface Livery {
  hull: number;
  bandA: number;
  bandB: number;
  /** bright humanitarian chevron stripes (hospital variant only) */
  chevron?: number;
  /** sensor/comms emissive accent — cyan for the EW picket, warm amber otherwise */
  sensorGlow: number;
  /** naval-scale house mark painted down the tankage flanks (e.g. 'HERITAGE');
   *  sized for another ship's optics — the decal game's typography register */
  houseMark?: string;
  /** a line of brush paint down each flank above the chine, one shell a word */
  motto?: string;
  /** the motto's paint; absent is brush red */
  mottoColor?: number;
  /** 'name' runs the pennant along each flank as a ship's name ('CCS TRACTATUS');
   *  absent or 'plate' paints it on a plate as a hull number ('CCS-42') */
  pennantStyle?: 'name' | 'plate';
  /** MPD plume tint (default violet); cooperative hulls run cooler blue */
  plumeColor?: number;
  /** if set, the drive shows a glowing core — e.g. a FAILING reactor (sickly red) */
  reactorGlow?: number;
  /** 'coated': a radar-absorbent coating over plate, satin paint with a little
   *  metal in it and bandA a metallic paint the armour takes too, chipping at its
   *  edges where it weathers. Absent is the working ship's bare plate. */
  finish?: 'coated';
}

export interface ShipSpec {
  /** canonical id — matches shipClasses.ts (e.g. 'custos-pf') so all tables key alike */
  id: string;
  /** short registry/URL handle (e.g. 'pf') */
  key: string;
  name: string;
  hullClass: string;
  pennantDefault: string;
  livery: Livery;
  radiatorPanelsPerSide: number;
  /** radius the radiator spar rides at — the hull's DRESSED half-width at
   *  that station, which is NOT the joinery radius HULL_IFACE reports: a
   *  gen-2 tankage interfaces at 1.0 and then wears tank-ring pipe runs out
   *  to 1.84. Seat against the joinery figure and the wings emerge from
   *  inside the hull. radiator-rig-check.mjs measures the drawn hull and
   *  fails if this drifts from it, so it cannot go quietly stale. */
  radiatorMountR?: number;
  /** station of the hull-number plates on both flanks. Default -1.9, the
   *  procedural frigates' foreship; an authored hull puts them clear of its fittings. */
  pennantZ?: number;
  sigil: boolean;
  modules: ModuleSpec[];
  /** authored hull: one GLB drawn once at ship level, plus the manifest that
   *  says where its modules and attach points are (docs/HULL-AUTHORING.md).
   *  Those modules belong in `modules` too - spread manifestModules() there. */
  hull?: HullRef;
  /** builder generation — v2 = the Assembly Grammar (joinery, constructed
   *  masses; docs/ASSEMBLY-GRAMMAR.md). v1 hulls stay untouched as the
   *  measurable baseline. Default 1. */
  generation?: number;
}

// ---- authored hulls: the manifest (docs/HULL-AUTHORING.md) ----
// A hull modelled outside this repo ships as a geometry-only GLB plus a
// manifest: the module positions and attach points the mesh cannot carry, and
// a measured z-profile of that mesh. Identity has to arrive SYNCHRONOUSLY -
// the GLB load is fire-and-forget (scene/parts.ts) and nothing reads its node
// names - so the manifest is imported as data and validated at module scope.
//
// Frame: engine units, engine axes. Forward = -Z, dorsal = +Y, spine +Z aft.

export const ATTACH_ROLES = ['plume', 'radiator', 'dock', 'hardpoint', 'light', 'rcs', 'retro'] as const;
export type AttachRole = (typeof ATTACH_ROLES)[number];

/** One authored module: where it sits, not what it looks like. */
export interface ManifestModule {
  id: string;
  kind: ModuleKind;
  /** centre along the spine */
  z: number;
  /** axial extent */
  length: number;
  /** authored half-extents. Never derived from `profile`, which would only
   *  re-assert the profile against itself - the checks skip an unboxed module
   *  and say so. */
  box?: { hw: number; hh: number };
  /** the named GLB nodes this module IS, for a hull with no .blend to carry a
   *  mod.* empty: z, length and box are their union AABB, written by
   *  tools/parts/hull_profile.mjs --write. The engine never reads it. */
  nodes?: string[];
  cols?: number;       // vls
  rows?: number;       // vls
}

/** A named point on the hull that hardware or an effect hangs off. `dir` is
 *  unit length (hullManifest normalizes it). */
export interface ManifestAttach {
  id: string;
  role: AttachRole;
  at: [number, number, number];
  dir: [number, number, number];
  /** plume: the nozzle's exit radius. The flame is a cone of fixed slenderness,
   *  so without this a capital's drive gets a hair-thin rod as long as the
   *  ship. Absent = scale off the drive module's length, as a procedural drive
   *  does. radiator: how far its vane reaches out of the hinge. dock: the
   *  port's own radius, which its hazard ring frames. */
  radius?: number;
  /** radiator: the heat pipes across its vane's chord, which the vane's face is painted with */
  pipes?: number;
}

/** The z-binned envelope of the shipped mesh. Bin k spans z0 + k*pitch ..
 *  z0 + (k+1)*pitch; the four bands are the same length, one sample per bin.
 *  `hw` draws the schematic outline, `rBody` is what hardware bolts to - a
 *  mast should not set a mount radius, which is why `r` is not that number. */
export interface HullProfile {
  pitch: number;
  z0: number;
  hw: number[];      // max |x| in the bin
  hh: number[];      // max |y| in the bin
  r: number[];       // max hypot(x, y) - the true silhouette, masts included
  rBody: number[];   // high-percentile radius - the body without its spikes
}

export interface HullManifest {
  /** manifest name, and the GLB it describes: public/art/parts/<hull>.glb */
  hull: string;
  frame: 'engine';
  spineLength: number;
  zRange: [number, number];
  /** draw the GLB yawed 180 degrees about the dorsal axis. The ingest chose
   *  each hull's forward and did not always choose the engine's; declaring it
   *  here rather than per-ship is what keeps the measured profile and the drawn
   *  hull in one frame, because tools/parts/hull_profile.mjs measures through
   *  the same rotation. Everything else in this manifest - modules, attach,
   *  profile - is in the frame the engine DRAWS, i.e. after this flip. */
  flip?: boolean;
  modules: ManifestModule[];
  attach: ManifestAttach[];
  profile: HullProfile;
  /** the material-name stems the GLB draws with, measured by hull_profile.mjs;
   *  absent, the hull is given every slot it could bind */
  slots?: string[];
  /** what wrote this manifest - "hand-written" is an honest answer */
  source: { tool: string; blend?: string; sha256?: string };
}

/** A ship's authored hull: the shared manifest plus this ship's own dressing.
 *  Two ships fly the same manifest with different glow, so glow cannot live
 *  in the measured half. */
export interface HullRef {
  manifest: HullManifest;
  /** emissive intensity for the glow slot (default = the port material's 2.2);
   *  tames a big glass canopy like the Liberdade cockpit */
  glow?: number;
  /** the part (src/scene/parts.ts) the rig swings at each att.radiator, in
   *  place of the spec rig; authored starboard, hinge on Z at its origin */
  radiator?: PartName;
  /** 'mesh': the GLB draws this ship's radiator wings itself (rad-white /
   *  rad-fin slots), so the procedural rig adds no hardware and posture drives
   *  only the radiator glow; radiatorPanelsPerSide still sizes the heat budget.
   *  Absent = the procedural rig, as for every other hull. */
  radiators?: 'mesh';
}

// r and rBody are derived from the same vertices as hw/hh, but a hand-written
// profile carries rounded numbers - enough slack for the rounding, not enough
// to hide a swapped axis.
const PROFILE_EPS = 1e-3;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isStr = (v: unknown): v is string => typeof v === 'string';
const isNumArray = (v: unknown): v is number[] => Array.isArray(v) && v.every(isNum);
const isNameArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((s: unknown) => isStr(s) && s.length > 0);
const isVec3 = (v: unknown): v is [number, number, number] =>
  Array.isArray(v) && v.length === 3 && v.every(isNum);
const isKind = (v: unknown): v is ModuleKind =>
  typeof v === 'string' && (MODULE_KINDS as readonly string[]).includes(v);
const isRole = (v: unknown): v is AttachRole =>
  typeof v === 'string' && (ATTACH_ROLES as readonly string[]).includes(v);

/**
 * Validate a manifest as it is imported, and throw naming the field - a bad
 * manifest should be a loud failure at boot, not a hull that quietly draws in
 * the wrong place. tools/parts/hull_profile.mjs --check runs this same
 * function over every manifest on disk.
 */
export function hullManifest(raw: unknown): HullManifest {
  if (!isObj(raw)) throw new Error('hull manifest: not an object');
  const hull = raw.hull;
  if (typeof hull !== 'string' || !hull) throw new Error('hull manifest: `hull` must be a non-empty name');
  const fail = (msg: string): Error => new Error(`hull manifest ${hull}: ${msg}`);

  if (raw.frame !== 'engine') throw fail(`frame must be "engine", got ${JSON.stringify(raw.frame)}`);
  const spineLength = raw.spineLength;
  if (!isNum(spineLength) || spineLength <= 0) throw fail('spineLength must be a positive number');
  const zr = raw.zRange;
  if (!isNumArray(zr) || zr.length !== 2) throw fail('zRange must be [min, max]');
  const zMin = zr[0]!, zMax = zr[1]!;
  if (zMin >= zMax) throw fail(`zRange ${zMin}..${zMax} is empty`);

  if (!Array.isArray(raw.modules)) throw fail('modules must be an array');
  const modIds = new Set<string>();
  const claimed = new Set<string>();
  const modules = raw.modules.map((rm, i): ManifestModule => {
    if (!isObj(rm)) throw fail(`module[${i}] is not an object`);
    const id = rm.id;
    if (typeof id !== 'string' || !id) throw fail(`module[${i}].id must be a non-empty string`);
    if (modIds.has(id)) throw fail(`duplicate module id "${id}"`);
    modIds.add(id);
    const kind = rm.kind;
    if (!isKind(kind)) throw fail(`module "${id}" has unknown kind ${JSON.stringify(kind)}`);
    const z = rm.z, length = rm.length;
    if (!isNum(z)) throw fail(`module "${id}".z must be a number`);
    if (z < zMin || z > zMax) throw fail(`module "${id}".z ${z} is outside zRange ${zMin}..${zMax}`);
    if (!isNum(length) || length <= 0) throw fail(`module "${id}".length must be positive`);
    const m: ManifestModule = { id, kind, z, length };
    if (rm.box !== undefined) {
      const box = rm.box;
      if (!isObj(box) || !isNum(box.hw) || !isNum(box.hh) || box.hw <= 0 || box.hh <= 0) {
        throw fail(`module "${id}".box must be { hw > 0, hh > 0 }`);
      }
      m.box = { hw: box.hw, hh: box.hh };
    }
    if (rm.nodes !== undefined) {
      const nodes = rm.nodes;
      if (!isNameArray(nodes) || !nodes.length) {
        throw fail(`module "${id}".nodes must be a non-empty array of node names`);
      }
      for (const n of nodes) {
        if (claimed.has(n)) throw fail(`module "${id}".nodes claims "${n}", which another module already holds`);
        claimed.add(n);
      }
      m.nodes = [...nodes];
    }
    if (rm.cols !== undefined) {
      if (!isNum(rm.cols)) throw fail(`module "${id}".cols must be a number`);
      m.cols = rm.cols;
    }
    if (rm.rows !== undefined) {
      if (!isNum(rm.rows)) throw fail(`module "${id}".rows must be a number`);
      m.rows = rm.rows;
    }
    return m;
  });

  if (!Array.isArray(raw.attach)) throw fail('attach must be an array');
  const attIds = new Set<string>();
  const attach = raw.attach.map((ra, i): ManifestAttach => {
    if (!isObj(ra)) throw fail(`attach[${i}] is not an object`);
    const id = ra.id;
    if (typeof id !== 'string' || !id) throw fail(`attach[${i}].id must be a non-empty string`);
    if (attIds.has(id)) throw fail(`duplicate attach id "${id}"`);
    attIds.add(id);
    if (!isRole(ra.role)) throw fail(`attach "${id}" has unknown role ${JSON.stringify(ra.role)}`);
    if (!isVec3(ra.at)) throw fail(`attach "${id}".at must be [x, y, z]`);
    if (!isVec3(ra.dir)) throw fail(`attach "${id}".dir must be [x, y, z]`);
    const len = Math.hypot(ra.dir[0], ra.dir[1], ra.dir[2]);
    if (len < 1e-6) throw fail(`attach "${id}".dir is a zero vector`);
    const radius = ra.radius;
    if (radius !== undefined && (!isNum(radius) || radius <= 0)) {
      throw fail(`attach "${id}".radius must be positive`);
    }
    if (radius !== undefined && ra.role !== 'plume' && ra.role !== 'radiator' && ra.role !== 'dock') {
      throw fail(`attach "${id}": only a plume, a radiator or a dock takes a radius`);
    }
    const pipes = ra.pipes;
    if (pipes !== undefined) {
      if (!isNum(pipes) || !Number.isInteger(pipes) || pipes <= 0) throw fail(`attach "${id}".pipes must be a positive integer`);
      if (ra.role !== 'radiator') throw fail(`attach "${id}": only a radiator takes pipes`);
    }
    const att: ManifestAttach = { id, role: ra.role, at: ra.at,
      dir: [ra.dir[0] / len, ra.dir[1] / len, ra.dir[2] / len] };
    if (radius !== undefined) att.radius = radius;
    if (isNum(pipes)) att.pipes = pipes;
    return att;
  });

  const p = raw.profile;
  if (!isObj(p)) throw fail('profile is required');
  const pitch = p.pitch, z0 = p.z0;
  if (!isNum(pitch) || pitch <= 0) throw fail('profile.pitch must be positive');
  if (!isNum(z0)) throw fail('profile.z0 must be a number');
  const hw = p.hw, hh = p.hh, r = p.r, rBody = p.rBody;
  if (!isNumArray(hw) || hw.length === 0) throw fail('profile.hw must be a non-empty number array');
  if (!isNumArray(hh) || hh.length !== hw.length) throw fail('profile.hh must match profile.hw in length');
  if (!isNumArray(r) || r.length !== hw.length) throw fail('profile.r must match profile.hw in length');
  if (!isNumArray(rBody) || rBody.length !== hw.length) throw fail('profile.rBody must match profile.hw in length');
  for (let i = 0; i < hw.length; i++) {
    const a = hw[i]!, b = hh[i]!, c = r[i]!, d = rBody[i]!;
    if (a < 0 || b < 0 || c < 0 || d < 0) throw fail(`profile bin ${i} has a negative extent`);
    // r is the hypotenuse over the same vertices as hw and hh, so r < max(hw, hh)
    // is impossible geometrically: it means an axis was swapped or the bands
    // were written out of step - the signature of a wrong frame chain.
    if (c + PROFILE_EPS < Math.max(a, b)) throw fail(`profile bin ${i}: r ${c} < max(hw ${a}, hh ${b})`);
    if (d > c + PROFILE_EPS) throw fail(`profile bin ${i}: rBody ${d} > r ${c}`);
  }
  const zEnd = z0 + hw.length * pitch;
  if (Math.abs(z0 - zMin) > pitch + PROFILE_EPS) throw fail(`profile starts at ${z0}, zRange at ${zMin}`);
  if (Math.abs(zEnd - zMax) > pitch + PROFILE_EPS) throw fail(`profile ends at ${zEnd}, zRange at ${zMax}`);

  const src = raw.source;
  if (!isObj(src) || typeof src.tool !== 'string' || !src.tool) {
    throw fail('source.tool must name what wrote this manifest');
  }
  const source: HullManifest['source'] = { tool: src.tool };
  if (typeof src.blend === 'string') source.blend = src.blend;
  if (typeof src.sha256 === 'string') source.sha256 = src.sha256;

  if (raw.flip !== undefined && typeof raw.flip !== 'boolean') throw fail('flip must be a boolean');
  const slots = raw.slots;
  if (slots !== undefined) {
    if (!isNameArray(slots)) throw fail('slots must be an array of material names');
    if (new Set(slots).size !== slots.length) throw fail('slots names a material twice');
  }

  const out: HullManifest = { hull, frame: 'engine', spineLength, zRange: [zMin, zMax], modules, attach,
                              profile: { pitch, z0, hw, hh, r, rBody }, source };
  if (raw.flip) out.flip = true;
  if (slots !== undefined) out.slots = [...slots];
  return out;
}

/** The manifest's modules as spec modules - identity and position, no
 *  geometry. Spread into ShipSpec.modules, where a ship can mix them with
 *  procedural ones. */
export function manifestModules(m: HullManifest): ModuleSpec[] {
  return m.modules.map((mm) => {
    const spec: ModuleSpec = { id: mm.id, kind: mm.kind, z: mm.z, length: mm.length, geometry: 'hull' };
    if (mm.cols !== undefined) spec.cols = mm.cols;
    if (mm.rows !== undefined) spec.rows = mm.rows;
    return spec;
  });
}

// ---- ShipSpec validation - the loader for a saved override or an imported
// export, the way hullManifest() is the loader for a hull manifest ----

function parseModuleSpec(raw: unknown, specId: string, i: number): ModuleSpec {
  const fail = (msg: string): Error => new Error(`ship spec ${specId}: module[${i}] ${msg}`);
  if (!isObj(raw)) throw fail('is not an object');
  const id = raw.id;
  if (!isStr(id) || !id) throw fail('id must be a non-empty string');
  const kind = raw.kind;
  if (!isKind(kind)) throw fail(`"${id}" has unknown kind ${JSON.stringify(kind)}`);
  const z = raw.z;
  if (!isNum(z)) throw fail(`"${id}".z must be a number`);
  const m: ModuleSpec = { id, kind, z };
  if (raw.geometry !== undefined) {
    if (raw.geometry !== 'hull') throw fail(`"${id}".geometry must be "hull" if present`);
    m.geometry = 'hull';
  }
  if (raw.cols !== undefined) {
    if (!isNum(raw.cols)) throw fail(`"${id}".cols must be a number`);
    m.cols = raw.cols;
  }
  if (raw.rows !== undefined) {
    if (!isNum(raw.rows)) throw fail(`"${id}".rows must be a number`);
    m.rows = raw.rows;
  }
  if (raw.expanded !== undefined) {
    if (!isBool(raw.expanded)) throw fail(`"${id}".expanded must be a boolean`);
    m.expanded = raw.expanded;
  }
  if (raw.length !== undefined) {
    if (!isNum(raw.length)) throw fail(`"${id}".length must be a number`);
    m.length = raw.length;
  }
  if (raw.size !== undefined) {
    if (!isNum(raw.size)) throw fail(`"${id}".size must be a number`);
    m.size = raw.size;
  }
  return m;
}

/** The longest house mark and motto a livery may carry, in characters: each paints canvases sized to its text. */
export const HOUSE_MARK_MAX = 16, MOTTO_MAX = 48;

function parseLivery(raw: unknown, specId: string): Livery {
  const fail = (msg: string): Error => new Error(`ship spec ${specId}: livery ${msg}`);
  if (!isObj(raw)) throw fail('is not an object');
  const hull = raw.hull, bandA = raw.bandA, bandB = raw.bandB, sensorGlow = raw.sensorGlow;
  if (!isNum(hull)) throw fail('hull must be a number');
  if (!isNum(bandA)) throw fail('bandA must be a number');
  if (!isNum(bandB)) throw fail('bandB must be a number');
  if (!isNum(sensorGlow)) throw fail('sensorGlow must be a number');
  const liv: Livery = { hull, bandA, bandB, sensorGlow };
  if (raw.chevron !== undefined) {
    if (!isNum(raw.chevron)) throw fail('chevron must be a number');
    liv.chevron = raw.chevron;
  }
  if (raw.houseMark !== undefined) {
    if (!isStr(raw.houseMark)) throw fail('houseMark must be a string');
    if ([...raw.houseMark].length > HOUSE_MARK_MAX) throw fail(`houseMark is longer than ${HOUSE_MARK_MAX} characters`);
    liv.houseMark = raw.houseMark;
  }
  if (raw.motto !== undefined) {
    if (!isStr(raw.motto)) throw fail('motto must be a string');
    if ([...raw.motto].length > MOTTO_MAX) throw fail(`motto is longer than ${MOTTO_MAX} characters`);
    liv.motto = raw.motto;
  }
  if (raw.mottoColor !== undefined) {
    if (!isNum(raw.mottoColor)) throw fail('mottoColor must be a number');
    liv.mottoColor = raw.mottoColor;
  }
  if (raw.plumeColor !== undefined) {
    if (!isNum(raw.plumeColor)) throw fail('plumeColor must be a number');
    liv.plumeColor = raw.plumeColor;
  }
  if (raw.reactorGlow !== undefined) {
    if (!isNum(raw.reactorGlow)) throw fail('reactorGlow must be a number');
    liv.reactorGlow = raw.reactorGlow;
  }
  if (raw.finish !== undefined) {
    if (raw.finish !== 'coated') throw fail("finish must be 'coated'");
    liv.finish = raw.finish;
  }
  if (raw.pennantStyle !== undefined) {
    if (raw.pennantStyle !== 'name' && raw.pennantStyle !== 'plate') throw fail("pennantStyle must be 'name' or 'plate'");
    liv.pennantStyle = raw.pennantStyle;
  }
  return liv;
}

/**
 * Validate a (possibly merged) ShipSpec against its own type, the way
 * hullManifest() validates a hull manifest - every field checked, throwing on
 * the first bad one so a malformed save or import fails loudly instead of
 * drawing wrong. The only runtime check on the SHIP_SPECS write path:
 * applyShipSpec, the dev-server save endpoint (tools/devserver/shipSpecPlugin.ts)
 * and the Import control (src/editor/ships.ts) all fail through here.
 */
export function parseShipSpec(raw: unknown): ShipSpec {
  if (!isObj(raw)) throw new Error('ship spec: not an object');
  const id = raw.id;
  if (!isStr(id) || !id) throw new Error('ship spec: id must be a non-empty string');
  const fail = (msg: string): Error => new Error(`ship spec ${id}: ${msg}`);
  const key = raw.key;
  if (!isStr(key) || !key) throw fail('key must be a non-empty string');
  const name = raw.name;
  if (!isStr(name) || !name) throw fail('name must be a non-empty string');
  const hullClass = raw.hullClass;
  if (!isStr(hullClass) || !hullClass) throw fail('hullClass must be a non-empty string');
  const pennantDefault = raw.pennantDefault;
  if (!isStr(pennantDefault) || !pennantDefault) throw fail('pennantDefault must be a non-empty string');
  const radiatorPanelsPerSide = raw.radiatorPanelsPerSide;
  if (!isNum(radiatorPanelsPerSide)) throw fail('radiatorPanelsPerSide must be a number');
  const sigil = raw.sigil;
  if (!isBool(sigil)) throw fail('sigil must be a boolean');
  if (!Array.isArray(raw.modules)) throw fail('modules must be an array');
  const modIds = new Set<string>();
  const modules = raw.modules.map((rm, i) => {
    const m = parseModuleSpec(rm, id, i);
    if (modIds.has(m.id)) throw fail(`duplicate module id "${m.id}"`);
    modIds.add(m.id);
    return m;
  });
  const livery = parseLivery(raw.livery, id);

  const spec: ShipSpec = { id, key, name, hullClass, pennantDefault, livery, radiatorPanelsPerSide, sigil, modules };
  if (raw.radiatorMountR !== undefined) {
    if (!isNum(raw.radiatorMountR)) throw fail('radiatorMountR must be a number');
    spec.radiatorMountR = raw.radiatorMountR;
  }
  if (raw.pennantZ !== undefined) {
    if (!isNum(raw.pennantZ)) throw fail('pennantZ must be a number');
    spec.pennantZ = raw.pennantZ;
  }
  if (raw.generation !== undefined) {
    if (!isNum(raw.generation)) throw fail('generation must be a number');
    spec.generation = raw.generation;
  }
  if (raw.hull !== undefined) {
    if (!isObj(raw.hull)) throw fail('hull must be an object');
    const hull: HullRef = { manifest: hullManifest(raw.hull.manifest) };
    if (raw.hull.glow !== undefined) {
      if (!isNum(raw.hull.glow)) throw fail('hull.glow must be a number');
      hull.glow = raw.hull.glow;
    }
    if (raw.hull.radiator !== undefined) {
      const vane = raw.hull.radiator;
      if (typeof vane !== 'string' || !(PART_NAMES as readonly string[]).includes(vane)) {
        throw fail(`hull.radiator ${JSON.stringify(vane)} is not a part in src/scene/partNames.ts`);
      }
      hull.radiator = vane as PartName;
      const bare = hull.manifest.attach.find((a) => a.role === 'radiator' && a.pipes === undefined);
      if (bare) throw fail(`hull.radiator swings a vane at att.radiator "${bare.id}", which declares no pipes`);
    }
    if (raw.hull.radiators !== undefined) {
      if (raw.hull.radiators !== 'mesh') throw fail('hull.radiators must be "mesh" if present');
      hull.radiators = 'mesh';
    }
    spec.hull = hull;
  }
  return spec;
}

// ---- the authored hulls this fleet flies ----
// Validated here, at import, so a manifest that does not describe its mesh is
// a failure at boot rather than a hull drawn in the wrong place.
export const KB_CARGOSHIP = hullManifest(kbCargoshipRaw);
export const KB_BATTLECRUISER = hullManifest(kbBattlecruiserRaw);
export const KB_COMMS_STATION = hullManifest(kbCommsStationRaw);
export const PUP_HULL = hullManifest(pupHullRaw);
// Authored in this repo (tools/parts/hulls/, ingested by its ingest_all.sh):
// original geometry, no licensed input. All four are the 148 m frigate hull.
export const CUSTOS_TOWER = hullManifest(custosTowerRaw);
export const CANNIBAL_HEAVY = hullManifest(cannibalHeavyRaw);
export const SPINE_TRUSS = hullManifest(spineTrussRaw);
export const LANCE_HULL = hullManifest(lanceRaw);

const NAVY = 0x24314f;
const MARS_RUST = 0xb0552e;
const AMBER = 0xffb04a;
const CYAN = 0x4fd3ff;

// Shared spine common to every Custos variant (stern +Z → bow -Z). Variants
// override the mission section (vls/pod), sensor fit, lance, and boarding bay.
function customsSpine(): ModuleSpec[] {
  return [
    { id: 'drive', kind: 'drive', z: 6.0 },
    { id: 'tankage', kind: 'tankage', z: 2.1 },
    { id: 'collar', kind: 'collar', z: 1.4 },
    { id: 'taper', kind: 'taper', z: -2.3 },
    { id: 'bow', kind: 'bow', z: -4.7 },
  ];
}

const PEWTER_LIVERY: Livery = { hull: 0xc4cad2, bandA: NAVY, bandB: MARS_RUST, sensorGlow: AMBER, houseMark: 'HERITAGE' };
// The Sich Stelarna rust palette, shared by the primitive raider and the Lance
// class; on an authored hull it lands on the paint-* slots as a palette swap.
const SICH_LIVERY: Livery = { hull: 0x6a5038, bandA: 0x884422, bandB: 0xcc7733, sensorGlow: 0xffaa44, houseMark: 'SICH' };

export const CUSTOS_SPECS: Record<string, ShipSpec> = {
  pf: {
    id: 'custos-pf', key: 'pf', name: 'Custos PF · patrol', hullClass: 'FF',
    pennantDefault: 'PF-1', livery: PEWTER_LIVERY, radiatorPanelsPerSide: 4, sigil: true,
    modules: [
      ...customsSpine(),
      { id: 'vls', kind: 'vls', z: -0.4, cols: 4, rows: 8 },
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: false },
      { id: 'lance', kind: 'lance', z: -6.3 },
      { id: 'bay', kind: 'boardingBay', z: 3.0 },
    ],
  },
  pfg: {
    id: 'custos-pfg', key: 'pfg', name: 'Custos PFg · guided-missile', hullClass: 'FF',
    pennantDefault: 'PF-14', livery: PEWTER_LIVERY, radiatorPanelsPerSide: 4, sigil: true,
    modules: [
      ...customsSpine(),
      { id: 'vls', kind: 'vls', z: -0.4, cols: 4, rows: 12 }, // 96-cell, boarding bay deleted
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: false },
      { id: 'lance', kind: 'lance', z: -6.3 },
    ],
  },
  pfe: {
    id: 'custos-pfe', key: 'pfe', name: 'Custos PFe · EW picket', hullClass: 'FF',
    pennantDefault: 'PF-22', radiatorPanelsPerSide: 4, sigil: true,
    livery: { hull: 0x9097a0, bandA: 0x1d6f86, bandB: 0x2a3340, sensorGlow: CYAN, houseMark: 'HERITAGE' },
    modules: [
      ...customsSpine(),
      { id: 'vls', kind: 'vls', z: -0.4, cols: 3, rows: 5 },
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: true },
      { id: 'lance', kind: 'lance', z: -6.3 },
      { id: 'bay', kind: 'boardingBay', z: 3.0 },
    ],
  },
  pfh: {
    id: 'custos-pfh', key: 'pfh', name: 'Custos PFh · hospital', hullClass: 'FF',
    pennantDefault: 'PF-31', radiatorPanelsPerSide: 4, sigil: true,
    livery: { hull: 0xe8eaee, bandA: NAVY, bandB: 0xff7a1a, chevron: 0xff5a1a, sensorGlow: AMBER, houseMark: 'HERITAGE' },
    modules: [
      ...customsSpine(),
      { id: 'pod', kind: 'medicalPod', z: -0.4 }, // disarmed: pod instead of vls, no lance
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: false },
      { id: 'bay', kind: 'boardingBay', z: 3.0 },
    ],
  },
};

// ---- up-ladder: the Heritage Order cruiser (one class above the frigate) ----
// "A longer stack with more modules" (per the frigate doc §I + the plan). Same
// builder, same radius (so the radiator rig still seats) — but a longer hull, a
// bigger drive block, a deeper 96+ launcher, a heavier Lance, more radiator
// panels, and a command livery (rust→deep-crimson accent). Pure data.
export const CRUISER_SPECS: Record<string, ShipSpec> = {
  ca: {
    id: 'heritage-ca', key: 'ca', name: 'Heritage Cruiser CA · monitor', hullClass: 'CA',
    pennantDefault: 'CA-3', radiatorPanelsPerSide: 6, sigil: true,
    livery: { hull: 0xb9c0cb, bandA: NAVY, bandB: 0x8c2f24, sensorGlow: AMBER, houseMark: 'HERITAGE' }, // crimson command band
    modules: [
      { id: 'drive', kind: 'drive', z: 8.0, size: 1.3 },
      { id: 'tankage', kind: 'tankage', z: 2.6, length: 7.6 },
      { id: 'collar', kind: 'collar', z: 1.0 },
      { id: 'vls', kind: 'vls', z: -0.6, cols: 4, rows: 14 },     // deep launcher
      { id: 'sensor', kind: 'sensor', z: -3.7, expanded: true },  // flagship sensors
      { id: 'taper', kind: 'taper', z: -3.5, length: 3.0 },
      { id: 'bow', kind: 'bow', z: -6.1, size: 1.2 },             // heavier prow
      { id: 'lance', kind: 'lance', z: -8.2, length: 3.4 },       // heavier spinal coilgun
      { id: 'bay', kind: 'boardingBay', z: 4.2 },
    ],
  },
};

// ---- down-ladder: the Heritage Order Cutter (smaller, older patrol hull) ----
// HMS Auriga (session-01). Below the frigate: short hull, small drive, a light
// kinetic launcher, a short Lance, few radiator panels. Same Heritage pewter
// livery + textures + radius (so decals/rig seat). Pure data.
export const CUTTER_SPECS: Record<string, ShipSpec> = {
  cutter: {
    id: 'heritage-cutter', key: 'cutter', name: 'Heritage Cutter CT · patrol', hullClass: 'CT',
    pennantDefault: 'CT-34', radiatorPanelsPerSide: 2, sigil: true,
    livery: PEWTER_LIVERY,
    modules: [
      { id: 'drive', kind: 'drive', z: 4.2, size: 0.72 },
      { id: 'tankage', kind: 'tankage', z: 1.4, length: 3.2 },
      { id: 'collar', kind: 'collar', z: 0.7 },
      { id: 'vls', kind: 'vls', z: -0.6, cols: 2, rows: 4 },
      { id: 'sensor', kind: 'sensor', z: -2.0, expanded: false },
      { id: 'taper', kind: 'taper', z: -1.9, length: 1.8 },
      { id: 'bow', kind: 'bow', z: -3.4, size: 0.82 },
      { id: 'lance', kind: 'lance', z: -4.6, length: 1.6 },
    ],
  },
};

// ---- civilian: Gens Iovia's corvette (Iuventius Pup) ----
// CCS Tractatus (the player, session-01), a Phoebe-Minor Q-ship dressed as a
// plain cargo corvette: a dark radar-absorbent grey coating, gunmetal strakes,
// gold kept to trim (the chine line, the hold's frames, the engine skirt), a cargo
// hold faired in dorsal, its Pet-MELiSSA chamber inboard, the
// Gens Iovia motto brushed down each flank, cool-blue engines. One authored hull:
// its modules come from the hull's manifest (manifestModules(PUP_HULL)).
export const CIVILIAN_SPECS: Record<string, ShipSpec> = {
  pup: {
    id: 'iuventius-pup', key: 'pup', name: 'Iuventius Pup · cooperative corvette', hullClass: 'CL',
    pennantDefault: 'CCS TRACTATUS', radiatorPanelsPerSide: 2, sigil: false,
    livery: { hull: 0x50565d, bandA: 0xa88a4a, bandB: 0x2b2e33, sensorGlow: AMBER, plumeColor: 0x6bb0ff,
      motto: 'HONORAMUS HOMINES OPERARIOS', mottoColor: 0xc9a85a, finish: 'coated', pennantStyle: 'name' },
    // One authored hull (tools/parts/build_pup_hull.py): a cargo hold under a
    // dorsal spine, a twin-bell drive in a gold skirt. Its Pet-MELiSSA is inboard,
    // seen by the crew and not from outside.
    hull: { manifest: PUP_HULL, glow: 0.4, radiator: 'pup-radiator' },
    modules: manifestModules(PUP_HULL),
    pennantZ: -0.55,               // between the fore flaps and the dock ring
  },
  esperanca: {
    id: 'cmt-esperanca', key: 'esperanca', name: 'Pequena Esperança · Marxiana transport', hullClass: 'CL',
    pennantDefault: 'CMT-71', radiatorPanelsPerSide: 3, sigil: false,
    // Worker-cooperative ochre + cooperative-red, gold cargo. FAILING reactor (red core).
    livery: { hull: 0x9a8258, bandA: 0x8a2f24, bandB: 0xc9a84e, sensorGlow: AMBER, plumeColor: 0x6bb0ff, reactorGlow: 0xff3a14, houseMark: 'MUTUALIS' },
    modules: [
      { id: 'drive', kind: 'drive', z: 5.4, size: 0.9 },
      { id: 'tankage', kind: 'tankage', z: 2.0, length: 3.4 },
      { id: 'collar', kind: 'collar', z: 1.0 },
      { id: 'cargoA', kind: 'cargoPod', z: 0.2 },
      { id: 'cargoB', kind: 'cargoPod', z: -1.6 },
      { id: 'melissa', kind: 'melissa', z: 1.6 },
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: false },
      { id: 'taper', kind: 'taper', z: -2.7, length: 1.8 },
      { id: 'bow', kind: 'bow', z: -3.9, size: 0.85 },
    ],
  },
};

// ---- other-faction escorts/patrols (Esperança scenario NPCs) ----
// Distinct silhouettes + liveries from the same builder. (Bespoke faction
// module kinds are a future refinement; this gets them off the greeble-box.)
export const FLEET_SPECS: Record<string, ShipSpec> = {
  // CLS Pobratym — Concordia League light missile escort (the player's wingman).
  pobratym: {
    id: 'concordia-escort', key: 'pobratym', name: 'Concordia Escort · light', hullClass: 'DD',
    pennantDefault: 'CLS-9', radiatorPanelsPerSide: 3, sigil: false,
    livery: { hull: 0x55806a, bandA: 0x2a5a44, bandB: 0x88ccaa, sensorGlow: 0x88ffcc, plumeColor: 0x88ffcc, houseMark: 'CONCORDIA' },
    modules: [
      { id: 'drive', kind: 'drive', z: 4.8, size: 0.78 },
      { id: 'tankage', kind: 'tankage', z: 1.8, length: 3.4 },
      { id: 'collar', kind: 'collar', z: 0.9 },
      { id: 'vls', kind: 'vls', z: -0.4, cols: 3, rows: 6 },
      { id: 'sensor', kind: 'sensor', z: -2.4, expanded: false },
      { id: 'taper', kind: 'taper', z: -2.3, length: 2.0 },
      { id: 'bow', kind: 'bow', z: -3.8, size: 0.85 },
      { id: 'lance', kind: 'lance', z: -5.0, length: 2.2 },
      { id: 'bay', kind: 'boardingBay', z: 3.2 },
    ],
  },
  // Sich Otaman — captured Armada battle cruiser, re-flagged by the Sich.
  // PILOT of the KitBash3D ingest lane (docs: geometry licensed & decimated,
  // materials/textures OURS via triplanar). No radiator rig: alien drivetrain.
  otaman: {
    id: 'sich-otaman', key: 'otaman', name: 'Sich Otaman · captured cruiser', hullClass: 'CB',
    pennantDefault: 'SS-1', radiatorPanelsPerSide: 0, sigil: false,
    livery: { hull: 0x7a5c40, bandA: 0x884422, bandB: 0xcc7733, sensorGlow: 0xffaa44, plumeColor: 0xff8844 },
    hull: { manifest: KB_BATTLECRUISER },
    modules: manifestModules(KB_BATTLECRUISER),
  },
  // CMT Liberdade — Marxiana convoy freighter (Minerva cargo ship ingest).
  // First hull onto the manifest: authored geometry, declared structure. Its
  // three modules are damage-targetable and draw their own SSD cells, and the
  // ingest's nose-aft orientation is corrected by the manifest's flip - it had
  // been flying tail-first since the ingest, which only showed once it had a
  // burn to fire (docs/HULL-AUTHORING.md).
  liberdade: {
    id: 'cmt-liberdade', key: 'liberdade', name: 'Liberdade · convoy freighter', hullClass: 'CL',
    pennantDefault: 'CMT-104', radiatorPanelsPerSide: 0, sigil: false,
    livery: { hull: 0x9a8258, bandA: 0x8a2f24, bandB: 0xc9a84e, sensorGlow: 0xffcf8f, plumeColor: 0x6bb0ff },
    hull: { manifest: KB_CARGOSHIP, glow: 0.55 },
    modules: manifestModules(KB_CARGOSHIP),
  },
  // Sich Stelarna rust-raider (heavy ram prow + long lance), drawn from
  // primitives: IKS Gadfly and the ?captains=N ring fly it. IKS Lance herself
  // is the lead ship of the Lance class and flies that hull (`lance` below).
  sich: {
    id: 'sich-patrol', key: 'sich', name: 'Sich Stelarna · raider', hullClass: 'DD',
    pennantDefault: 'SS-12', radiatorPanelsPerSide: 3, sigil: false,
    livery: { ...SICH_LIVERY, plumeColor: 0xff8844 },
    modules: [
      { id: 'drive', kind: 'drive', z: 4.6, size: 0.85 },
      { id: 'tankage', kind: 'tankage', z: 1.6, length: 3.0 },
      { id: 'collar', kind: 'collar', z: 0.8 },
      { id: 'vls', kind: 'vls', z: -0.5, cols: 2, rows: 6 },
      { id: 'sensor', kind: 'sensor', z: -2.2, expanded: false },
      { id: 'taper', kind: 'taper', z: -2.0, length: 1.6 },
      { id: 'bow', kind: 'bow', z: -3.4, size: 1.0 },
      { id: 'lance', kind: 'lance', z: -5.0, length: 2.6 },
    ],
  },
};

// ---- the fleet of ten (overnight expansion, 2026-07-06) ----
// Eight pure-data hulls up and down every faction's ladder, one re-liveried
// KitBash capital, one ingested station. Zero new builder code.
export const EXPANSION_SPECS: Record<string, ShipSpec> = {
  // 1. Heritage Order dreadnought — the biggest spine the grammar has made.
  dn: {
    id: 'heritage-dn', key: 'dn', name: 'Heritage Dreadnought · Custodian', hullClass: 'DN',
    pennantDefault: 'DN-1', radiatorPanelsPerSide: 8, sigil: true,
    livery: { hull: 0xb9c0cb, bandA: NAVY, bandB: 0x8c2f24, sensorGlow: AMBER, houseMark: 'HERITAGE' },
    modules: [
      { id: 'drive', kind: 'drive', z: 10.5, size: 1.5 },
      { id: 'tankage', kind: 'tankage', z: 3.2, length: 10.4 },
      { id: 'collar', kind: 'collar', z: -2.4 },
      { id: 'vls', kind: 'vls', z: -3.9, cols: 6, rows: 14 },
      { id: 'sensor', kind: 'sensor', z: -6.6, expanded: true },
      { id: 'taper', kind: 'taper', z: -6.4, length: 3.2 },
      { id: 'bow', kind: 'bow', z: -9.2, size: 1.35 },
      { id: 'lance', kind: 'lance', z: -12.0, length: 4.0 },
      { id: 'bay', kind: 'boardingBay', z: 5.6 },
    ],
  },
  // 2. Heritage corvette — smallest warship on the ladder; one radiator panel.
  fs: {
    id: 'heritage-fs', key: 'fs', name: 'Heritage Corvette · Vedette', hullClass: 'FS',
    pennantDefault: 'FS-7', radiatorPanelsPerSide: 1, sigil: true, livery: PEWTER_LIVERY,
    modules: [
      { id: 'drive', kind: 'drive', z: 3.4, size: 0.55 },
      { id: 'tankage', kind: 'tankage', z: 1.0, length: 2.2 },
      { id: 'collar', kind: 'collar', z: 0.4 },
      { id: 'vls', kind: 'vls', z: -0.7, cols: 2, rows: 3 },
      { id: 'sensor', kind: 'sensor', z: -1.6, expanded: false },
      { id: 'taper', kind: 'taper', z: -1.7, length: 1.4 },
      { id: 'bow', kind: 'bow', z: -2.9, size: 0.6 },
      { id: 'lance', kind: 'lance', z: -3.9, length: 1.2 },
    ],
  },
  // 3. Custos PFb breacher — the VLS traded for a SECOND boarding bay.
  pfb: {
    id: 'custos-pfb', key: 'pfb', name: 'Custos PFb · breacher', hullClass: 'FF',
    pennantDefault: 'PF-9', radiatorPanelsPerSide: 4, sigil: true,
    livery: { hull: 0xc4cad2, bandA: NAVY, bandB: 0xd97a2b, sensorGlow: AMBER }, // boarding orange
    modules: [
      ...customsSpine(),
      { id: 'bayFwd', kind: 'boardingBay', z: -0.4 },
      { id: 'bayAft', kind: 'boardingBay', z: 3.0 },
      { id: 'sensor', kind: 'sensor', z: -2.8, expanded: false },
      { id: 'lance', kind: 'lance', z: -6.3 },
    ],
  },
  // 4. Concordia heavy escort — the Pobratym's bigger sister.
  ddg: {
    id: 'concordia-ddg', key: 'ddg', name: 'Concordia Escort · heavy', hullClass: 'DDG',
    pennantDefault: 'CLS-2', radiatorPanelsPerSide: 4, sigil: false,
    livery: { hull: 0x4f7862, bandA: 0x1f4634, bandB: 0x9fd8bb, sensorGlow: 0x88ffcc, plumeColor: 0x88ffcc, houseMark: 'CONCORDIA' },
    modules: [
      { id: 'drive', kind: 'drive', z: 5.2, size: 0.95 },
      { id: 'tankage', kind: 'tankage', z: 1.9, length: 3.8 },
      { id: 'collar', kind: 'collar', z: 0.9 },
      { id: 'vls', kind: 'vls', z: -0.5, cols: 4, rows: 10 },
      { id: 'sensor', kind: 'sensor', z: -2.6, expanded: true },
      { id: 'taper', kind: 'taper', z: -2.5, length: 2.0 },
      { id: 'bow', kind: 'bow', z: -4.0, size: 0.9 },
      { id: 'lance', kind: 'lance', z: -5.4, length: 2.4 },
      { id: 'bay', kind: 'boardingBay', z: 3.4 },
    ],
  },
  // 5. Sich Chaika — glass cannon: a lance comically long for her hull.
  chaika: {
    id: 'sich-chaika', key: 'chaika', name: 'Sich Chaika · lance boat', hullClass: 'FS',
    pennantDefault: 'SS-33', radiatorPanelsPerSide: 2, sigil: false,
    livery: { hull: 0x5c4632, bandA: 0x2e2018, bandB: 0xd07a2c, sensorGlow: 0xffaa44, plumeColor: 0xff8844, houseMark: 'SICH' },
    modules: [
      { id: 'drive', kind: 'drive', z: 4.2, size: 0.7 },
      { id: 'tankage', kind: 'tankage', z: 1.3, length: 2.6 },
      { id: 'collar', kind: 'collar', z: 0.6 },
      { id: 'vls', kind: 'vls', z: -0.5, cols: 2, rows: 4 },
      { id: 'sensor', kind: 'sensor', z: -1.9, expanded: false },
      { id: 'taper', kind: 'taper', z: -1.8, length: 1.5 },
      { id: 'bow', kind: 'bow', z: -3.0, size: 0.85 },
      { id: 'lance', kind: 'lance', z: -5.2, length: 3.6 },
    ],
  },
  // 6. Marxiana tanker — TWO tank sections, two docking collars, no teeth.
  cisterna: {
    id: 'cmt-cisterna', key: 'cisterna', name: 'Cisterna · propellant tanker', hullClass: 'AO',
    pennantDefault: 'CMT-12', radiatorPanelsPerSide: 5, sigil: false,
    livery: { hull: 0x9a8258, bandA: 0xb03a20, bandB: 0xc9a84e, sensorGlow: AMBER, plumeColor: 0x6bb0ff, houseMark: 'MUTUALIS' },
    modules: [
      { id: 'drive', kind: 'drive', z: 7.6, size: 0.95 },
      { id: 'tankA', kind: 'tankage', z: 3.4, length: 4.2 },
      { id: 'collarA', kind: 'collar', z: 0.9 },
      { id: 'tankB', kind: 'tankage', z: -1.6, length: 4.2 },
      { id: 'collarB', kind: 'collar', z: -4.1 },
      { id: 'sensor', kind: 'sensor', z: -5.3, expanded: false },
      { id: 'taper', kind: 'taper', z: -5.6, length: 1.6 },
      { id: 'bow', kind: 'bow', z: -7.0, size: 0.8 },
    ],
  },
  // 7. Liga Aurea long-hauler — the Pup stretched: twin pods + MELiSSA.
  mula: {
    id: 'liga-mula', key: 'mula', name: 'Mula · cooperative long-hauler', hullClass: 'CL',
    pennantDefault: 'CCS-19', radiatorPanelsPerSide: 2, sigil: false,
    livery: { hull: 0xb8895a, bandA: MARS_RUST, bandB: 0x7a5a36, sensorGlow: AMBER, plumeColor: 0x6bb0ff, houseMark: 'LIGA AUREA' },
    modules: [
      { id: 'drive', kind: 'drive', z: 6.0, size: 0.7 },
      { id: 'tankage', kind: 'tankage', z: 2.0, length: 4.0 },
      { id: 'collar', kind: 'collar', z: -0.2 },
      { id: 'cargoA', kind: 'cargoPod', z: -1.4 },
      { id: 'cargoB', kind: 'cargoPod', z: -3.2 },
      { id: 'melissa', kind: 'melissa', z: 1.2 },
      { id: 'taper', kind: 'taper', z: -2.8, length: 2.4 },
      { id: 'sensor', kind: 'sensor', z: -4.6, expanded: false },
      { id: 'bow', kind: 'bow', z: -5.0, size: 0.62 },
    ],
  },
  // 8. Hospital ship — white hull, red chevrons, twin med pods, no weapons.
  ah: {
    id: 'hospital-ah', key: 'ah', name: 'Misericórdia · hospital ship', hullClass: 'AH',
    pennantDefault: 'AH-1', radiatorPanelsPerSide: 5, sigil: false,
    livery: { hull: 0xe6e9ee, bandA: 0xb03226, bandB: 0xf2f4f7, chevron: 0xff5040, sensorGlow: 0x9fd4ff, houseMark: 'MISERICORDIA' },
    modules: [
      { id: 'drive', kind: 'drive', z: 7.0, size: 1.0 },
      { id: 'tankage', kind: 'tankage', z: 2.6, length: 5.6 },
      { id: 'collar', kind: 'collar', z: -0.4 },
      { id: 'podA', kind: 'medicalPod', z: -1.6 },
      { id: 'podB', kind: 'medicalPod', z: -3.3 },
      { id: 'sensor', kind: 'sensor', z: -4.9, expanded: false },
      { id: 'taper', kind: 'taper', z: -4.8, length: 2.2 },
      { id: 'bow', kind: 'bow', z: -6.6, size: 1.0 },
      { id: 'bay', kind: 'boardingBay', z: 4.4 },
    ],
  },
  // 9. CLS Zhoda — the League's OWN Armada cruiser: same GLB, green flag.
  zhoda: {
    id: 'concordia-zhoda', key: 'zhoda', name: 'CLS Zhoda · League flagship', hullClass: 'CB',
    pennantDefault: 'CLS-1', radiatorPanelsPerSide: 0, sigil: false,
    livery: { hull: 0x628a74, bandA: 0x2a5a44, bandB: 0x88ccaa, sensorGlow: 0x88ffcc, plumeColor: 0x88ffcc, houseMark: 'CONCORDIA' },
    hull: { manifest: KB_BATTLECRUISER, glow: 1.4 },
    modules: manifestModules(KB_BATTLECRUISER),
  },
  // 10. Relay Vigil — the Esperança Window's comms relay (Minerva array).
  relay: {
    id: 'relay-vigil', key: 'relay', name: 'Relay Vigil · comms station', hullClass: 'STN',
    pennantDefault: 'RV-1', radiatorPanelsPerSide: 0, sigil: false,
    livery: { hull: 0xb9c0cb, bandA: NAVY, bandB: 0x8c8f96, sensorGlow: CYAN },
    hull: { manifest: KB_COMMS_STATION, glow: 1.6 },
    modules: manifestModules(KB_COMMS_STATION),
  },
};

// ---- GENERATION 2 — the Assembly Grammar lane (measured deltas vs v1) ----
// Same mission, same livery, same spine data as the v1 baseline; only the
// construction grammar differs. Audit them side by side: spec:pup vs spec:pup2.
export const V2_SPECS: Record<string, ShipSpec> = {
  ca2: {
    id: 'heritage-ca-v2', key: 'ca2', name: 'Heritage Cruiser CA · v2 grammar', hullClass: 'CA',
    pennantDefault: 'CA-3', radiatorPanelsPerSide: 6, sigil: true, generation: 2,
    radiatorMountR: 1.84,   // tank-ring pipe runs, z -1.2..6.4
    livery: { hull: 0xb9c0cb, bandA: NAVY, bandB: 0x8c2f24, sensorGlow: AMBER, houseMark: 'HERITAGE' },
    modules: [
      { id: 'drive', kind: 'drive', z: 8.0, size: 1.3 },
      { id: 'tankage', kind: 'tankage', z: 2.6, length: 7.6 },
      { id: 'collar', kind: 'collar', z: 1.0 },
      { id: 'vls', kind: 'vls', z: -0.6, cols: 4, rows: 14 },
      { id: 'sensor', kind: 'sensor', z: -3.7, expanded: true },
      { id: 'taper', kind: 'taper', z: -3.5, length: 3.0 },
      { id: 'bow', kind: 'bow', z: -6.1, size: 1.2 },
      { id: 'lance', kind: 'lance', z: -8.2, length: 3.4 },
      { id: 'bay', kind: 'boardingBay', z: 4.2 },
    ],
  },
  pup2: {
    id: 'iuventius-pup-v2', key: 'pup2', name: 'Iuventius Pup · v2 grammar', hullClass: 'CL',
    pennantDefault: 'CCS-7', radiatorPanelsPerSide: 2, sigil: false, generation: 2,
    radiatorMountR: 1.82,   // tank-ring pipe runs, z -1.9..5.1
    livery: { hull: 0xb8895a, bandA: MARS_RUST, bandB: 0x7a5a36, sensorGlow: AMBER, plumeColor: 0x6bb0ff, houseMark: 'LIGA AUREA' },
    modules: [
      { id: 'drive', kind: 'drive', z: 4.4, size: 0.62 },
      { id: 'tankage', kind: 'tankage', z: 1.5, length: 3.0 },
      { id: 'collar', kind: 'collar', z: 0.7 },
      { id: 'cargo', kind: 'cargoPod', z: -0.1 },
      { id: 'melissa', kind: 'melissa', z: 1.4 },
      { id: 'sensor', kind: 'sensor', z: -1.9, expanded: false },
      { id: 'taper', kind: 'taper', z: -1.9, length: 1.6 },
      { id: 'bow', kind: 'bow', z: -3.1, size: 0.7 },
    ],
  },
};

// ---- the authored frigates (tools/parts/hulls/) ----
// Four builders written for the Custos brief, each a whole hull drawn from one
// GLB. Each carries its radiator wings in the mesh (hull.radiators 'mesh') in
// cruise posture; panels-per-side is the wing count per side and sizes the heat
// budget only. The manifests' VLS modules carry no cell grid (the ingest has no
// empty for it), so the 64 cells each builder models are stated here.
function authoredModules(m: HullManifest): ModuleSpec[] {
  return manifestModules(m).map((mm) => (mm.kind === 'vls' ? { ...mm, cols: 4, rows: 16 } : mm));
}

export const AUTHORED_SPECS: Record<string, ShipSpec> = {
  // HOMV Custos (PF-1), the Custos-class Patrol Frigate - "Tower" - and the
  // player's ship (src/cruise/playerShip.ts). Canon: ../4xciv
  // phase-h/08-vessels/01-custos-class-frigate.md. Two panels a side is also
  // the thermal budget the player's heat model was tuned on (the Pup's).
  tower: {
    id: 'custos-tower', key: 'tower', name: 'Custos PF · Tower', hullClass: 'FF',
    pennantDefault: 'PF-1', livery: PEWTER_LIVERY, radiatorPanelsPerSide: 2, sigil: false,
    hull: { manifest: CUSTOS_TOWER, radiators: 'mesh' },
    modules: authoredModules(CUSTOS_TOWER),
  },
  // Class and role ratified by Erik 2026-09-30: an early Heritage Order
  // warship on Starship / Super Heavy launch-hardware lineage. Not
  // Custos-class. In service by 2050 at the latest (Era 1, pre-Boron-Cliff):
  // fission-electric (NEP), electric thrusters on a fission reactor behind a
  // shadow shield, not fusion. One four-panel accordion wing a side.
  cannibal: {
    id: 'heritage-cannibal', key: 'cannibal', name: 'Cannibal Heavy · early warship', hullClass: 'FF',
    pennantDefault: 'HC-2', radiatorPanelsPerSide: 4, sigil: false,
    livery: { hull: 0xc9ced5, bandA: NAVY, bandB: MARS_RUST, sensorGlow: AMBER },
    hull: { manifest: CANNIBAL_HEAVY, radiators: 'mesh' },
    modules: authoredModules(CANNIBAL_HEAVY),
  },
  // Class and role ratified by Erik 2026-09-30: the first deep asteroid
  // explorers, the reach carried as tankage you can see. In service by 2075
  // at the latest: first-generation p-11B aneutronic fusion, just after the
  // 2072-09-22 Boron Cliff.
  spinetruss: {
    id: 'heritage-spinetruss', key: 'spinetruss', name: 'Spine & Truss · deep explorer', hullClass: 'CL',
    pennantDefault: 'HX-3', radiatorPanelsPerSide: 2, sigil: false,
    livery: { hull: 0xd2d5da, bandA: NAVY, bandB: 0xd97a2b, sensorGlow: AMBER },
    hull: { manifest: SPINE_TRUSS, radiators: 'mesh' },
    modules: authoredModules(SPINE_TRUSS),
  },
  // The Lance class, ratified by Erik 2026-09-30: a Sich Stelarna strike /
  // interdiction frigate built around the spinal coilgun, named for its lead
  // ship IKS Lance (SS-12), the Sich hussar frigate and raider of the
  // Esperança Window. Not Custos-class and not Heritage Order - the Heritage
  // Custos PF-3, HOMV Old Lance, is a different ship. In service by 2075 at
  // the latest: first-generation p-11B aneutronic fusion, just after the
  // 2072-09-22 Boron Cliff. Was `heritage-lance` (LEGACY_SPEC_IDS).
  lance: {
    id: 'sich-lance', key: 'lance', name: 'IKS Lance · hussar frigate', hullClass: 'FF',
    pennantDefault: 'SS-12', radiatorPanelsPerSide: 2, sigil: false,
    livery: SICH_LIVERY,
    hull: { manifest: LANCE_HULL, radiators: 'mesh' },
    modules: authoredModules(LANCE_HULL),
  },
};

/** Every buildable hull, keyed by short id. resolveSpec + the PoC/traffic look up here. */
export const SHIP_SPECS: Record<string, ShipSpec> = {
  ...CUSTOS_SPECS, ...CUTTER_SPECS, ...CRUISER_SPECS, ...CIVILIAN_SPECS, ...FLEET_SPECS,
  ...EXPANSION_SPECS, ...V2_SPECS, ...AUTHORED_SPECS,
};

/** Retired spec ids and the id each became, so an exported spec file or a
 *  saved override written under the old id still lands on its ship. */
export const LEGACY_SPEC_IDS: Readonly<Record<string, string>> = {
  'heritage-lance': 'sich-lance',
};

function specKeyForId(id: string): string | undefined {
  const current = LEGACY_SPEC_IDS[id] ?? id;
  for (const [key, s] of Object.entries(SHIP_SPECS)) if (s.id === current) return key;
  return undefined;
}

/**
 * The one writable path onto SHIP_SPECS. Merges `patch`'s top-level fields
 * over the existing spec, validates the result with parseShipSpec, and
 * replaces the entry so every fresh SHIP_SPECS[key] lookup (resolveSpec,
 * findSpec, a freshly spawned traffic ship) sees it. A nested field (a livery
 * color, one module) is the caller's job to merge into a full `livery` or
 * `modules` value first - this only validates and swaps, it does not deep-merge.
 * A spec already built into a THREE.Group keeps its old geometry until
 * whatever rebuilds it runs again; src/editor/ships.ts rebuilds its own live
 * preview on every render, which is the only rebuild path this function owns.
 * DERIVED_SCHEMATICS in src/main.ts is a snapshot taken at import time and does
 * not re-derive on a live edit - true before this function existed too.
 */
export function applyShipSpec(id: string, patch: Partial<ShipSpec>): ShipSpec {
  const key = specKeyForId(id);
  if (!key) throw new Error(`applyShipSpec: unknown ship spec id "${id}"`);
  const current = SHIP_SPECS[key]!;
  const merged: unknown = { ...current, ...patch, ...(patch.id !== undefined && LEGACY_SPEC_IDS[patch.id] ? { id: current.id } : {}) };
  const validated = parseShipSpec(merged);
  SHIP_SPECS[key] = validated;
  return validated;
}

/** The known ids applyShipSpec (and the save endpoint) will accept. */
export const SHIP_SPEC_IDS: readonly string[] = Object.values(SHIP_SPECS).map((s) => s.id);

// Saved overrides (src/editor/ships.ts's Save button, via
// tools/devserver/shipSpecPlugin.ts) apply over the literals here, through the
// same applyShipSpec a live edit uses, so the two can never disagree on shape.
// Empty until a save happens; docs/editor-01-ships.md "Save and Import".
for (const [overrideId, patch] of Object.entries(shipSpecOverridesRaw as Record<string, Partial<ShipSpec>>)) {
  // A data file must never blank the app at boot; skip stale ids with a warning.
  try {
    applyShipSpec(overrideId, patch);
  } catch (err) {
    console.warn('shipSpecOverrides: skipped', overrideId, err instanceof Error ? err.message : String(err));
  }
}

export function resolveSpec(variant?: string | ShipSpec): ShipSpec {
  if (variant && typeof variant === 'object') return variant;
  return SHIP_SPECS[variant ?? 'pf'] ?? SHIP_SPECS.pf!;
}
